import ScrollSmoother from 'gsap/ScrollSmoother';

const PATCH_FLAG = Symbol.for('zmbz.scrollSmoother.isInViewport.patched');

type SmootherProto = typeof ScrollSmoother.prototype & {
	isInViewport?: (target: unknown, threshold?: number, horizontal?: boolean) => boolean;
	[key: symbol]: unknown;
};

/**
 * Hardens `ScrollSmoother.prototype.isInViewport` so the built-in
 * auto-scroll-on-focus interceptor cannot crash on targets that have no
 * `getBoundingClientRect` (Text nodes, `document`, detached elements, and a
 * few iOS in-app WebView edge cases). Treating an unresolvable target as
 * "already in viewport" short-circuits ScrollSmoother's boolean chain and
 * prevents a follow-up `scrollTo()` call from blowing up on the same target.
 *
 * Safe to call multiple times; only patches once per process.
 */
export function patchScrollSmootherIsInViewport(): void {
	const proto = ScrollSmoother?.prototype as SmootherProto | undefined;
	if (!proto || typeof proto.isInViewport !== 'function') {
		return;
	}
	if (proto[PATCH_FLAG]) {
		return;
	}

	const original = proto.isInViewport;

	proto.isInViewport = function patchedIsInViewport(
		target: unknown,
		threshold?: number,
		horizontal?: boolean,
	): boolean {
		const resolved = typeof target === 'string' ? document.querySelector(target) : target;

		if (
			!resolved ||
			typeof resolved !== 'object' ||
			typeof (resolved as Element).getBoundingClientRect !== 'function'
		) {
			return true;
		}

		try {
			return original.call(this, target, threshold, horizontal);
		} catch {
			return true;
		}
	};

	proto[PATCH_FLAG] = true;
}
